import { refreshTokensIfNeeded, subscribeAccessTokenSet } from "@/api/service"
import { subscribeAppReturn } from "@/helpers/appReturn"
import { getTokenFromStorage } from "@/helpers/jwt"
import {
  CELL_CONNECT_KIND,
  CELL_REFUSAL_REASON,
  ICellChannelLogEntry,
  ICellChannelState,
  ICellChannelStateWriter,
  ICellConfirmation,
  ICellsClient,
  ICellsClientDeps
} from "@/store/apis/cellsAPI/cells.types"
import {
  classifyConnectError,
  confirmationDelayMs,
  isNewToken,
  reopenDelayMs
} from "@/store/apis/cellsAPI/cellsPolicy"
import { createSocketBase } from "@/store/apis/socket.factory"
import {
  ICellMembershipEvent,
  ICellRef,
  SOCKET_EVENTS
} from "@/store/apis/socketRegistry"

const SERVER_CLOSE = "io server disconnect"
const CLIENT_CLOSE = "io client disconnect"
const LOG_SIZE = 5

const isObject = (value: unknown): value is object =>
  typeof value === "object" && value !== null

const cellOfPayload = (payload: unknown): ICellRef | null =>
  isObject(payload) &&
  "mapId" in payload &&
  typeof payload.mapId === "string" &&
  "x" in payload &&
  typeof payload.x === "number" &&
  "y" in payload &&
  typeof payload.y === "number"
    ? { mapId: payload.mapId, x: payload.x, y: payload.y }
    : null

const toLogEntry = (
  event: string,
  payload: unknown,
  receivedAtMs: number
): ICellChannelLogEntry => ({
  receivedAtMs,
  event,
  cell: cellOfPayload(payload),
  data: isObject(payload) && "data" in payload ? payload.data : payload
})

export const createCellsClient = (deps: ICellsClientDeps): ICellsClient => {
  const socket = createSocketBase("cells")
  const confirmationListeners = new Set<
    (confirmation: ICellConfirmation) => void
  >()
  let subscribers = 0
  let session = 0
  let write: ICellChannelStateWriter | null = null
  let pendingKind = CELL_CONNECT_KIND.FIRST
  let connectionKind = CELL_CONNECT_KIND.FIRST
  let firstOfConnection = false
  let failures = 0
  let handedToken: string | null = null
  let refusedToken: string | null = null
  let lastReturnAtMs: number | null = null
  let cancelReopen: (() => void) | null = null
  let stopTokenWait: (() => void) | null = null
  let stopAppReturn: (() => void) | null = null

  const writeState: ICellChannelStateWriter = (recipe) => write?.(recipe)
  const setStatus = (status: ICellChannelState["status"]): void =>
    writeState((state) => ({ ...state, status }))
  const clearReopenTimer = (): void => {
    cancelReopen?.()
    cancelReopen = null
  }
  const clearTokenWait = (): void => {
    stopTokenWait?.()
    stopTokenWait = null
  }

  const reopen = (kind: CELL_CONNECT_KIND): void => {
    const instance = socket.instance
    if (subscribers === 0 || instance === null || instance.active) {
      return
    }
    clearReopenTimer()
    clearTokenWait()
    pendingKind = kind
    setStatus("reconnecting")
    instance.connect()
  }

  const scheduleReopen = (delayMs: number): void => {
    if (cancelReopen !== null) {
      return
    }
    cancelReopen = deps.schedule(() => {
      cancelReopen = null
      reopen(CELL_CONNECT_KIND.REOPEN)
    }, delayMs)
  }

  const reopenWithRefreshedToken = async (): Promise<void> => {
    const startedIn = session
    await deps.refreshTokensIfNeeded()
    if (
      startedIn === session &&
      stopTokenWait !== null &&
      isNewToken(deps.getAccessToken(), refusedToken)
    ) {
      reopen(CELL_CONNECT_KIND.REOPEN_AFTER_UNAUTHORIZED)
    }
  }

  const onUnauthorized = (): void => {
    refusedToken = handedToken
    setStatus("refused")
    clearTokenWait()
    stopTokenWait = deps.subscribeAccessTokenSet((token) => {
      if (isNewToken(token, refusedToken)) {
        reopen(CELL_CONNECT_KIND.REOPEN_AFTER_UNAUTHORIZED)
      }
    })
    void reopenWithRefreshedToken()
  }

  const onConnectError = (error: Error): void => {
    const reason = classifyConnectError(error, socket.instance?.active ?? false)
    if (reason === CELL_REFUSAL_REASON.UNAUTHORIZED) {
      onUnauthorized()
      return
    }
    if (reason === CELL_REFUSAL_REASON.UNAVAILABLE) {
      setStatus("reconnecting")
      scheduleReopen(reopenDelayMs(failures, deps.random))
      failures += 1
    }
  }

  const onDisconnect = (reason: string): void => {
    if (reason === CLIENT_CLOSE) {
      return
    }
    setStatus("reconnecting")
    if (reason === SERVER_CLOSE) {
      scheduleReopen(reopenDelayMs(0, deps.random))
      return
    }
    pendingKind = CELL_CONNECT_KIND.AUTOMATIC
  }

  const onConnect = (): void => {
    connectionKind = pendingKind
    firstOfConnection = true
    failures = 0
    clearTokenWait()
    setStatus("connected")
  }

  const onMembership = (event: ICellMembershipEvent): void => {
    const cell = event.data.cell
    const delayMs = confirmationDelayMs({
      kind: connectionKind,
      firstOfConnection,
      msSinceReturn:
        lastReturnAtMs === null ? null : deps.nowMs() - lastReturnAtMs,
      random: deps.random
    })
    firstOfConnection = false
    writeState((state) => ({ ...state, confirmedCell: cell }))
    confirmationListeners.forEach((listener) => listener({ cell, delayMs }))
  }

  const onAnyEvent = (event: string, payload: unknown): void => {
    if (event === SOCKET_EVENTS.cellMembership) {
      return
    }
    const entry = toLogEntry(event, payload, deps.nowMs())
    writeState((state) => ({
      ...state,
      recentEvents: [entry, ...state.recentEvents].slice(0, LOG_SIZE)
    }))
  }

  const onAppReturn = (): void => {
    lastReturnAtMs = deps.nowMs()
    if (stopTokenWait !== null) {
      void reopenWithRefreshedToken()
    }
  }

  socket._bind(SOCKET_EVENTS.cellMembership, onMembership)

  return {
    start(writer) {
      write = writer
      subscribers += 1
      if (subscribers > 1) {
        return
      }
      session += 1
      pendingKind = CELL_CONNECT_KIND.FIRST
      failures = 0
      stopAppReturn = deps.subscribeAppReturn(onAppReturn)
      socket._init({
        auth: () => {
          handedToken = deps.getAccessToken()
          return { token: handedToken ?? "" }
        },
        onConnect,
        onConnectError,
        onDisconnect,
        onAny: onAnyEvent
      })
      socket.addSub()
    },
    stop() {
      if (subscribers === 0) {
        return
      }
      subscribers -= 1
      if (subscribers > 0) {
        return
      }
      session += 1
      clearReopenTimer()
      clearTokenWait()
      stopAppReturn?.()
      stopAppReturn = null
      write = null
      socket.removeSub()
    },
    bindCellEvent: (event, handler) => socket._bind(event, handler),
    onConfirmation(listener) {
      confirmationListeners.add(listener)
      return () => {
        confirmationListeners.delete(listener)
      }
    },
    isStarted: () => subscribers > 0
  }
}

export const cellsSocket = createCellsClient({
  getAccessToken: () => getTokenFromStorage("accessToken"),
  refreshTokensIfNeeded,
  subscribeAccessTokenSet,
  subscribeAppReturn,
  nowMs: () => Date.now(),
  random: Math.random,
  schedule: (run, delayMs) => {
    const timer = setTimeout(run, delayMs)
    return () => clearTimeout(timer)
  }
})
